import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ProfileActions.scss';
import aboutIcon from './img/about.png';
import copyIcon from './img/copy.png';
import reportIcon from './img/report.png';
import blockIcon from './img/block.png';
import shareIcon from './img/share.png';
import AboutModal from './AboutModal';
import { useAuth } from '../../contexts/AuthContext';
import ReportsModal from '../Reports/Reports';
import { getAccessToken } from '../../awsAuth';

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  'http://localhost:5000';

const ProfileActions = ({ onClose, userInfo, onBlockedChange }) => {
  const { userId: currentUserId } = useAuth();

  const [copySuccess, setCopySuccess] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [loadingBlock, setLoadingBlock] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const [showReport, setShowReport] = useState(false);

  const targetUserId = userInfo?.id;

  const modalRef = useRef(null);
  const firstFocusableRef = useRef(null);

  const reportOpenTimerRef = useRef(null);
  const reportRequestedRef = useRef(false);

  const handleRequestClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose?.();
    }, 240);
  }, [isClosing, onClose]);

  useEffect(() => {
    const body = document.body;
    const prev = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = prev || '';
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => firstFocusableRef.current?.focus(), 10);
    const onKey = (e) => {
      if (e.key === 'Escape') handleRequestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onKey);
    };
  }, [handleRequestClose]);

  const onOverlayMouseDown = useCallback(
    (e) => {
      if (!modalRef.current) return;
      if (!modalRef.current.contains(e.target)) handleRequestClose();
    },
    [handleRequestClose]
  );

  useEffect(() => {
    const checkBlocked = async () => {
      try {
        if (!targetUserId || !currentUserId) return;

        const token = await getAccessToken();
        if (!token) return;

        const res = await fetch(
          `${API_BASE}/api/block/check/${targetUserId}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          console.error('Не вдалося перевірити статус блокування:', data);
          return;
        }

        const data = await res.json();
        const blocked = !!data.isBlocked;
        setIsBlocked(blocked);
      } catch (err) {
        console.error('Не вдалося перевірити статус блокування:', err);
      }
    };

    checkBlocked();
  }, [targetUserId, currentUserId]);

  const handleBlockToggle = async () => {
    if (!currentUserId || !targetUserId || currentUserId === targetUserId) return;
    if (loadingBlock) return;

    setLoadingBlock(true);
    const goingToBlock = !isBlocked;

    try {
      const token = await getAccessToken();

      if (!token) {
        alert('Потрібна авторизація');
        return;
      }

      let url;
      let method;
      let body;

      if (goingToBlock) {
        url = `${API_BASE}/api/block/${targetUserId}`;
        method = 'POST';
        body = JSON.stringify({});
      } else {
        url = `${API_BASE}/api/block`;
        method = 'DELETE';
        body = JSON.stringify({
          blockedId: targetUserId,
        });
      }

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        console.error('Помилка блокування / розблокування:', data);
        alert(data.error || 'Сталася помилка');
        return;
      }

      setIsBlocked(goingToBlock);

      if (typeof onBlockedChange === 'function') {
        onBlockedChange(goingToBlock);
      }
    } catch (err) {
      console.error('Помилка блокування:', err);
      alert('Сталася помилка при блокуванні');
    } finally {
      setLoadingBlock(false);
    }
  };

  const openReportAndCloseActions = useCallback(() => {
    if (reportRequestedRef.current) return;
    reportRequestedRef.current = true;

    if (!isClosing) {
      setIsClosing(true);
    }

    reportOpenTimerRef.current = setTimeout(() => {
      setShowReport(true);
      reportRequestedRef.current = false;
    }, 240);
  }, [isClosing]);

  useEffect(() => {
    return () => {
      if (reportOpenTimerRef.current) {
        clearTimeout(reportOpenTimerRef.current);
      }
    };
  }, []);

  const handleShareProfile = useCallback(async () => {
    try {
      const url = window.location.href;
      const username = userInfo?.username ? `@${userInfo.username}` : 'Профіль';
      const title = userInfo?.username ? `Профіль ${userInfo.username}` : 'Профіль';

      if (navigator.share) {
        await navigator.share({
          title,
          text: `Подивись профіль ${username} у Cvanto`,
          url,
        });
        return;
      }

      await navigator.clipboard.writeText(url);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 1800);
      alert('Поділитись не підтримується на цьому пристрої. Посилання скопійовано.');
    } catch (err) {
      console.error('Не вдалося поділитися профілем:', err);
      alert('Не вдалося поділитися профілем');
    }
  }, [userInfo]);

  const handleAction = (action) => {
    switch (action) {
      case 'about':
        setShowAbout(true);
        break;

      case 'share':
        handleShareProfile();
        break;

      case 'copy': {
        const url = window.location.href;
        navigator.clipboard
          .writeText(url)
          .then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 1800);
          })
          .catch((err) => {
            console.error('Не вдалося скопіювати URL:', err);
            alert('Не вдалося скопіювати URL');
          });
        break;
      }

      case 'report':
        openReportAndCloseActions();
        break;

      case 'block':
        handleBlockToggle();
        break;

      default:
        break;
    }
  };

  return (
    <>
      {!showReport && (
        <div
          className={`profile-actions-overlay ${isClosing ? 'is-closing' : ''}`}
          onMouseDown={onOverlayMouseDown}
        >
          <div className="profile-actions-backdrop" />
          <div
            className="profile-actions-shell"
            role="dialog"
            aria-modal="true"
            aria-label="Меню дій профілю"
            ref={modalRef}
          >
            <div className="profile-actions">
              <button
                ref={firstFocusableRef}
                className="pa-item"
                onClick={() => handleAction('about')}
              >
                <img src={aboutIcon} alt="Про акаунт" />
                <span>Про акаунт</span>
              </button>

              <button
                className="pa-item pa-item--mobile-only"
                onClick={() => handleAction('share')}
              >
                <img src={shareIcon} alt="Поділитись профілем" />
                <span>Поділитись профілем</span>
              </button>

              <button className="pa-item" onClick={() => handleAction('copy')}>
                <img src={copyIcon} alt="Копіювати URL" />
                <span>{copySuccess ? 'Скопійовано!' : 'Копіювати URL профілю'}</span>
              </button>

              <button className="pa-item" onClick={() => handleAction('report')}>
                <img src={reportIcon} alt="Поскаржитись" />
                <span>Поскаржитись</span>
              </button>

              <button
                className="pa-item danger"
                onClick={() => handleAction('block')}
                disabled={loadingBlock}
              >
                <img src={blockIcon} alt="Блокування" />
                <span>
                  {loadingBlock
                    ? 'Завантаження...'
                    : isBlocked
                    ? 'Розблокувати акаунт'
                    : 'Заблокувати акаунт'}
                </span>
              </button>

              <button className="pa-cancel" onClick={handleRequestClose}>
                Скасувати
              </button>
            </div>
          </div>
        </div>
      )}

      {showAbout && (
        <AboutModal
          onClose={() => setShowAbout(false)}
          username={userInfo?.username}
          avatar={userInfo?.avatar}
          createdAt={userInfo?.createdAt}
        />
      )}

      {showReport && (
        <ReportsModal
          open={showReport}
          onClose={() => {
            setShowReport(false);
            onClose?.();
          }}
          targetType="user"
          targetId={targetUserId}
          context="profile"
          username={userInfo?.username}
        />
      )}
    </>
  );
};

export default ProfileActions;